import React, { ChangeEvent, useEffect } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm, Controller } from 'react-hook-form';
import { PayloadAction } from '@reduxjs/toolkit';
import InputWithLabel from '@ui-parts/InputWithLabel';
import styled from 'styled-components';
import Button from '@ui-elements/Button';
import { Link, Typography } from '@mui/material';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';

import { FetchAdminRolesResponse, SigninAdminParams } from '@mytypes/api';
import { useAppSelector, useAppDispatch } from '~/App/hooks';
import { signinAdmin, fetchAdminRoles } from '~/apiTs';
import { PALETTE, Z_INDEX } from '~/assets/styles/base';
import LogoImage from '~/assets/images/logo-gmoask.png';
import { SESSION_STORAGES, LOCAL_STORAGES } from '~/constants';
import { ROUTES } from '~/constants/routes';
import {
  adminEmailValidationRule,
  signinPasswordValidationRule,
} from '~/constants/validationRules';

const AdminLoginFormContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const Img = styled.img`
  display: block;
  margin: 14px auto 20px auto;
  width: 258px;
`;

const InputCenterBox = styled.div`
  width: 300px;
  margin: 0 auto;
`;

const CenterBox = styled.div`
  text-align: center;
  margin: 0 auto;
`;

const LoginButton = styled(Button)`
  display: block;
  width: 280px;
  margin: 25px auto 20px auto;
  background: ${PALETTE.primary.main};
`;

const RegisterTypography = styled(Typography)`
  display: block;
  width: 280px;
  margin: 20px auto 15px auto;
  text-align: center;
`;

const StyledAlert = styled.div`
  width: max-content;
  line-height: 1.5;
  background-color: ${PALETTE.error.light};
  color: ${PALETTE.error.main};
  padding: 11px 16px;
  margin: 17px auto 11px;
`;

const StyledTypography = styled(Typography)`
  display: flex;
  align-items: flex-start;
  flex-wrap: nowrap;
  white-space: pre-line;
  svg {
    font-size: 25px;
    margin: -2px 10px 0;
  }
`;

const AdminSignin = () => {
  const dispatch = useAppDispatch();
  const { admin } = useAppSelector((state) => state);
  const validation = useAppSelector((state) => state.validation.signin);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { control, handleSubmit } = useForm({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: {
      email: '',
      password: '',
    },
  });

  const onSubmit = (data: SigninAdminParams) => {
    dispatch(signinAdmin(data)) as unknown as PayloadAction<
      undefined,
      string,
      { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
      never
    >;
  };

  /**
   * fetch administrator's roles,
   * and set them to local storage.
   */
  const setAdminRoles = async () => {
    const {
      payload: { result },
    } = (await dispatch(fetchAdminRoles())) as unknown as PayloadAction<
      FetchAdminRolesResponse,
      string,
      { arg: undefined; requestId: string; requestStatus: 'fulfilled' },
      never
    >;
    localStorage.setItem(LOCAL_STORAGES.ADMIN_ROLES, result.roles.toString());
  };

  useEffect(() => {
    // axiosErrorをクリアしないとリダイレクトループしてしまう可能性があるので、ここでクリアしてあげる。
    dispatch({ type: 'axiosError/initializeAxiosError' });
  }, []);

  useEffect(() => {
    (async () => {
      if (!validation?.errors?.authentication?.invalid && admin.id !== 0) {
        await setAdminRoles();
        const redirectURL = sessionStorage.getItem(
          SESSION_STORAGES.ADMIN_UNAUTHORIZED_ERROR_REFERRER
        );
        if (redirectURL) {
          navigate(redirectURL, {
            state: { referrer: window.location.pathname },
          });
        } else {
          navigate(ROUTES.ADMIN.MYPAGE);
        }
      }
    })();
  }, [admin.id]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <AdminLoginFormContainer data-testid="Admin-Signin-AdminLoginFormContainer">
        <Img src={LogoImage} alt="GMO Ask" title="GMO Ask" data-testid="Admin-Signin-Logo" />
        <RegisterTypography variant="title1">{t('adminSignin.adminView')}</RegisterTypography>
        {validation?.errors?.authentication?.invalid && (
          <StyledAlert color="error">
            <StyledTypography variant="body3">
              <WarningRoundedIcon />
              <span data-testid="Admin-Signin-Authentication-Error-Message">
                {validation.errors.authentication.message}
              </span>
            </StyledTypography>
          </StyledAlert>
        )}
        <InputCenterBox>
          <Controller
            name="email"
            control={control}
            rules={adminEmailValidationRule}
            render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => {
              const inputProps = {
                label: t('adminSignin.email'),
                name,
                type: 'text',
                value,
                errorMessage: error?.message,
                width: '300px',
                textAlign: 'left',
                handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                  onChange(e);
                },
                handleBlur: onBlur,
                error: Boolean(error),
                errorZIndex: Z_INDEX.tooltip,
                dataTestId: 'Admin-Signin-email',
                inputProps: { maxLength: 100 },
              };
              return <InputWithLabel {...inputProps} ref={ref} />;
            }}
          />
          <Controller
            name="password"
            control={control}
            rules={signinPasswordValidationRule}
            render={({ field: { onChange, onBlur, value, name, ref }, fieldState: { error } }) => {
              const inputProps = {
                label: t('adminSignin.password'),
                name,
                type: 'password',
                value,
                errorMessage: error?.message,
                width: '300px',
                textAlign: 'left',
                handleChange: (e: ChangeEvent<HTMLInputElement>) => {
                  onChange(e);
                },
                handleBlur: onBlur,
                error: Boolean(error),
                errorZIndex: Z_INDEX.tooltip,
                dataTestId: 'Admin-Signin-password',
                inputProps: { maxLength: 24 },
              };
              return <InputWithLabel {...inputProps} ref={ref} />;
            }}
          />
        </InputCenterBox>
        <LoginButton variant="contained" type="submit" data-testid="Admin-Signin-LoginButton">
          {t('adminSignin.login')}
        </LoginButton>
        <CenterBox>
          <Link
            component={RouterLink}
            to={ROUTES.ADMIN.PASSWORD_RESET_REQUEST}
            underline="none"
            variant="body3"
            data-testid="Admin-Signin-ForgetPasswordLink"
          >
            {t('adminSignin.forgetPassord')}
          </Link>
        </CenterBox>
      </AdminLoginFormContainer>
    </form>
  );
};

export default AdminSignin;
